import React, { useState, useMemo } from 'react';
import GenerateProposalModal from './GenerateProposalModal';

// Minimal shapes the picker needs — reports and documents both expose an id and
// a display name plus an optional timestamp/status.
interface SourceReport {
  id: string;
  title: string;
  createdAt?: string | Date;
  workflowType?: string;
}
interface SourceDocument {
  id: string;
  name: string;
  status?: string;
}

interface CreateProposalModalProps {
  customerId: string;
  projectId?: string;
  reports: SourceReport[];
  documents: SourceDocument[];
  token: string | null;
  onClose: () => void;
}

/**
 * Proposals-tab entry point for creating a proposal. Step 1 lets the user pick
 * any combination of existing reports and documents as the source; Step 2 hands
 * those off to GenerateProposalModal (the shared template picker + generator).
 */
export const CreateProposalModal: React.FC<CreateProposalModalProps> = ({
  customerId,
  projectId,
  reports,
  documents,
  token,
  onClose,
}) => {
  const [step, setStep] = useState<'sources' | 'templates'>('sources');
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);

  // Only completed documents have extractable content worth feeding the AI.
  const selectableDocuments = useMemo(
    () => documents.filter((d) => !d.status || d.status === 'completed'),
    [documents]
  );

  const totalSelected = selectedReportIds.length + selectedDocumentIds.length;

  const toggle = (
    id: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  if (step === 'templates') {
    return (
      <GenerateProposalModal
        reportIds={selectedReportIds}
        documentIds={selectedDocumentIds}
        customerId={customerId}
        projectId={projectId}
        token={token}
        onClose={onClose}
        onBack={() => setStep('sources')}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-dark-surface rounded-lg shadow-xl border border-gray-200 dark:border-dark-border w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create Proposal</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Choose the reports and/or documents to build this proposal from.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6 min-h-0">
          {/* Reports */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Reports {reports.length > 0 && <span className="text-gray-400 font-normal">({reports.length})</span>}
            </h3>
            {reports.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">No reports available.</p>
            ) : (
              <div className="space-y-2">
                {reports.map((r) => {
                  const checked = selectedReportIds.includes(r.id);
                  return (
                    <label
                      key={r.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        checked
                          ? 'border-primary bg-primary/5 dark:bg-primary/10'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(r.id, setSelectedReportIds)}
                        className="h-4 w-4 text-primary border-gray-300 rounded"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{r.title}</p>
                        {r.createdAt && (
                          <p className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleDateString()}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Documents */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Documents {selectableDocuments.length > 0 && <span className="text-gray-400 font-normal">({selectableDocuments.length})</span>}
            </h3>
            {selectableDocuments.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">No documents available.</p>
            ) : (
              <div className="space-y-2">
                {selectableDocuments.map((d) => {
                  const checked = selectedDocumentIds.includes(d.id);
                  return (
                    <label
                      key={d.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        checked
                          ? 'border-primary bg-primary/5 dark:bg-primary/10'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(d.id, setSelectedDocumentIds)}
                        className="h-4 w-4 text-primary border-gray-300 rounded"
                      />
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1 min-w-0">{d.name}</p>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {totalSelected === 0 ? 'Select at least one source' : `${totalSelected} selected`}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={() => setStep('templates')}
              disabled={totalSelected === 0}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              Next: Choose Templates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProposalModal;
